import { FREQUENCIES } from 'composables/HearingTest/types/HearingTestTypes';

export interface AudioContextSetup {
  context: AudioContext;
  gainNode: GainNode;
  pannerNode: StereoPannerNode;
}

export function createAudioContext(): AudioContextSetup {
  const AudioContextClass =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const context = new AudioContextClass();
  const gainNode = context.createGain();
  const pannerNode = context.createStereoPanner();

  gainNode.connect(pannerNode);
  pannerNode.connect(context.destination);

  return {
    context,
    gainNode,
    pannerNode,
  };
}

export async function loadAudioFile(context: AudioContext, url: string): Promise<AudioBuffer> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch audio file: ${response.status} ${response.statusText}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    return await context.decodeAudioData(arrayBuffer);
  } catch {
    throw new Error(`Failed to load audio file: ${url}`);
  }
}

export function createSourceNode(
  context: AudioContext,
  buffer: AudioBuffer,
  loop = true
): AudioBufferSourceNode {
  const sourceNode = context.createBufferSource();
  sourceNode.buffer = buffer;
  sourceNode.loop = loop;

  return sourceNode;
}

export function setVolume(gainNode: GainNode, gainValue: number, when?: number): void {
  const targetTime = when ?? gainNode.context.currentTime;
  const clampedGain = Math.max(0, Math.min(1, gainValue));
  gainNode.gain.setValueAtTime(clampedGain, targetTime);
}

export function setPanning(
  pannerNode: StereoPannerNode,
  ear: 'left' | 'right',
  when?: number
): void {
  const targetTime = when ?? pannerNode.context.currentTime;
  const panValue = ear === 'left' ? -1 : 1;
  pannerNode.pan.setValueAtTime(panValue, targetTime);
}

export function stopAudioSource(sourceNode: AudioBufferSourceNode | null): void {
  if (!sourceNode) return;

  try {
    sourceNode.stop();
    sourceNode.disconnect();
  } catch {
    // Source might already be stopped
  }
}

export async function resumeAudioContext(context: AudioContext): Promise<void> {
  if (context.state === 'suspended') {
    try {
      await context.resume();
    } catch {
      throw new Error('AudioContext blocked by browser. User interaction required.');
    }
  }
}

export function getAudioFilePath(frequency: number): string {
  return `/audio/hearing-test/warble-tones/warble_${frequency}.mp3`;
}

export function isValidFrequency(frequency: number): boolean {
  const supportedFrequencies = [
    FREQUENCIES.HZ_500,
    FREQUENCIES.HZ_1000,
    FREQUENCIES.HZ_2000,
    FREQUENCIES.HZ_4000,
  ];
  return supportedFrequencies.includes(frequency);
}

export async function cleanupAudioContext(
  audioContextSetup: AudioContextSetup | null
): Promise<void> {
  if (!audioContextSetup) return;
  const { context, gainNode, pannerNode } = audioContextSetup;
  try {
    gainNode.disconnect();
    pannerNode.disconnect();

    if (context.state !== 'closed') {
      await context.close();
    }
  } catch (error) {
    console.error('Error during audio context cleanup:', error);
  }
}
